import React, { useMemo } from 'react';
import {
  ArrowRight,
  Facebook,
  FileCheck2,
  Files,
  Globe2,
  Instagram,
  Linkedin,
  PackageCheck,
  Palette,
  Sparkles,
  Tags,
  Twitter,
  Youtube
} from 'lucide-react';
import { LANGUAGE_OPTIONS, normalizeLanguage } from '../../locales';
import { openCookieSettings } from '../../services/cookiePreferences';
import { localizeInternalHref, localizeSeoHref } from '../../utils/localeRouting';
import { SITE_SOCIAL_LINKS } from '../../utils/seoDiscovery';
import { normalizeTheme } from '../../utils/theme.js';
import StocktagBrandLogo from '../StocktagBrandLogo.jsx';
import LanguageAlternatesNav from '../LanguageAlternatesNav.jsx';
import ThemeToggle from '../ThemeToggle';
import {
  PUBLIC_MARKETING_ANCHORS,
  PUBLIC_MARKETING_ROUTES
} from '../../data/publicMarketingNavigation.js';
import {
  PUBLIC_MARKETING_FOOTER_COPY_BN,
  PUBLIC_MARKETING_FOOTER_FLOW_BN
} from '../../data/i18n/publicChrome.bn.js';
import {
  PUBLIC_MARKETING_FOOTER_COPY_HI,
  PUBLIC_MARKETING_FOOTER_FLOW_HI
} from '../../data/i18n/publicChrome.hi.js';
import {
  PUBLIC_MARKETING_FOOTER_COPY_PT,
  PUBLIC_MARKETING_FOOTER_FLOW_PT
} from '../../data/i18n/publicChrome.pt.js';
import '../../styles/PublicMarketingShell.css';

const FOOTER_COPY_BY_LANGUAGE = {
  en: {
    ctaEyebrow: 'Start your next batch here', ctaTitle: 'Publish your images with marketplace-ready metadata.',
    ctaBody: 'Create, review, and export everything from one workspace.', studio: 'Open Metadata Studio',
    seePricing: 'See pricing', product: 'Product', tools: 'Tools', resources: 'Resources', account: 'Account',
    company: 'Company', legal: 'Legal', footerNavigation: 'Footer navigation', platformExports: 'Platform exports', metadataTools: 'Metadata tools',
    tagline: 'A complete metadata workflow for stock creators.', about: 'About', editorial: 'Editorial policy',
    contact: 'Contact', privacy: 'Privacy notice', terms: 'Terms of service', social: 'Stocktag social profiles'
  },
  tr: {
    ctaEyebrow: 'Sıradaki dosya grubunuz hazır', ctaTitle: 'Görsellerinizi pazar yerlerine uygun meta verilerle yayınlayın.',
    ctaBody: 'Tek çalışma alanında üretin, inceleyin ve dışa aktarın.', studio: 'Metadata Studio’yu Aç',
    seePricing: 'Planları incele', product: 'Ürün', tools: 'Araçlar', resources: 'Kaynaklar', account: 'Hesap',
    company: 'Şirket', legal: 'Yasal', footerNavigation: 'Alt menü', platformExports: 'Platforma uygun çıktılar', metadataTools: 'Meta veri araçları',
    tagline: 'Stok içerik üreticileri için baştan sona meta veri iş akışı.', about: 'Hakkımızda', editorial: 'Editoryal politika',
    contact: 'İletişim', privacy: 'Gizlilik bildirimi', terms: 'Kullanım koşulları', social: 'Stocktag sosyal profilleri'
  },
  es: {
    ctaEyebrow: 'Empieza tu próximo lote', ctaTitle: 'Prepara metadatos listos para cada marketplace.',
    ctaBody: 'Genera, revisa y exporta desde un único espacio de trabajo.', studio: 'Abrir Metadata Studio',
    seePricing: 'Ver precios', product: 'Producto', tools: 'Herramientas', resources: 'Recursos', account: 'Cuenta',
    company: 'Empresa', legal: 'Información legal', footerNavigation: 'Navegación del pie de página', platformExports: 'Exportaciones por plataforma', metadataTools: 'Herramientas de metadatos',
    tagline: 'Todo el flujo de metadatos para creadores de contenido stock.', about: 'Sobre Stocktag', editorial: 'Política editorial',
    contact: 'Contacto', privacy: 'Política de privacidad', terms: 'Condiciones de servicio', social: 'Redes sociales de Stocktag'
  },
  fr: {
    ctaEyebrow: 'Préparez votre prochain lot', ctaTitle: 'Publiez vos visuels avec des métadonnées prêtes pour chaque place de marché.',
    ctaBody: 'Générez, vérifiez et exportez tout depuis le même espace de travail.', studio: 'Ouvrir Metadata Studio',
    seePricing: 'Voir les tarifs', product: 'Produit', tools: 'Outils', resources: 'Ressources', account: 'Compte',
    company: 'L’entreprise', legal: 'Informations légales', footerNavigation: 'Navigation du pied de page', platformExports: 'Exports par plateforme', metadataTools: 'Outils de métadonnées',
    tagline: 'Toutes les métadonnées dont les contributeurs de banques d’images ont besoin, dans un seul outil.', about: 'À propos de Stocktag', editorial: 'Politique éditoriale',
    contact: 'Contact', privacy: 'Avis de confidentialité', terms: 'Conditions d’utilisation', social: 'Réseaux sociaux de Stocktag'
  },
  de: {
    ctaEyebrow: 'Bereit für den nächsten Dateistapel?', ctaTitle: 'Marktplatzfertige Metadaten für Ihre Bilder – ohne Umwege.',
    ctaBody: 'Erstellen, prüfen und exportieren Sie Ihre Metadaten in einem Arbeitsbereich.', studio: 'Metadata Studio öffnen',
    seePricing: 'Tarife ansehen', product: 'Produkt', tools: 'Tools', resources: 'Ratgeber', account: 'Konto',
    company: 'Unternehmen', legal: 'Rechtliches', footerNavigation: 'Footer-Navigation', platformExports: 'Exporte für Marktplätze', metadataTools: 'Metadaten-Tools',
    tagline: 'Metadaten-Workflow für Stock-Contributor – vom Upload bis zum Export.', about: 'Über Stocktag', editorial: 'Redaktionsrichtlinie',
    contact: 'Kontakt', privacy: 'Datenschutzhinweis', terms: 'Nutzungsbedingungen', social: 'Social-Media-Kanäle von Stocktag'
  },
  it: {
    ctaEyebrow: 'Il tuo prossimo batch parte da qui', ctaTitle: 'Trasforma i contenuti visivi in metadati pronti per i marketplace.',
    ctaBody: 'Genera, rivedi ed esporta in un unico spazio di lavoro.', studio: 'Apri Metadata Studio',
    seePricing: 'Scopri i prezzi', product: 'Prodotto', tools: 'Strumenti', resources: 'Risorse', account: 'Account',
    company: 'Azienda', legal: 'Informazioni legali', footerNavigation: 'Navigazione del piè di pagina', platformExports: 'Esportazioni per piattaforma', metadataTools: 'Strumenti per i metadati',
    tagline: 'Un workflow dedicato ai metadati per contributor stock.', about: 'Chi siamo', editorial: 'Politica editoriale',
    contact: 'Contatti', privacy: 'Informativa sulla privacy', terms: 'Termini di servizio', social: 'Profili social di Stocktag'
  },
  id: {
    ctaEyebrow: 'Batch berikutnya dimulai di sini', ctaTitle: 'Ubah aset visual menjadi metadata siap marketplace.',
    ctaBody: 'Buat, tinjau, dan ekspor semuanya dari satu ruang kerja.', studio: 'Buka Metadata Studio',
    seePricing: 'Lihat harga', product: 'Produk', tools: 'Alat', resources: 'Referensi', account: 'Akun',
    company: 'Perusahaan', legal: 'Informasi hukum', footerNavigation: 'Navigasi bagian bawah', platformExports: 'Ekspor per platform', metadataTools: 'Alat metadata',
    tagline: 'Alur metadata lengkap untuk kreator stok.', about: 'Tentang Stocktag', editorial: 'Kebijakan editorial',
    contact: 'Kontak', privacy: 'Pemberitahuan privasi', terms: 'Ketentuan layanan', social: 'Media sosial Stocktag'
  },
  th: {
    ctaEyebrow: 'เริ่มงานชุดถัดไปได้ที่นี่', ctaTitle: 'เปลี่ยนแอสเซ็ตให้เป็นเมทาดาทาที่พร้อมลงมาร์เก็ตเพลส',
    ctaBody: 'สร้าง ตรวจทาน และส่งออกได้จากพื้นที่ทำงานเดียว', studio: 'เปิด Metadata Studio',
    seePricing: 'ดูแพ็กเกจและราคา', product: 'ผลิตภัณฑ์', tools: 'เครื่องมือ', resources: 'คู่มือและแหล่งข้อมูล', account: 'บัญชี',
    company: 'บริษัท', legal: 'ข้อมูลทางกฎหมาย', footerNavigation: 'เมนูส่วนท้าย', platformExports: 'ส่งออกตามแพลตฟอร์ม', metadataTools: 'เครื่องมือเมทาดาทา',
    tagline: 'เวิร์กโฟลว์เมทาดาทาที่สร้างมาเพื่อครีเอเตอร์สายสต็อก', about: 'เกี่ยวกับเรา', editorial: 'นโยบายบรรณาธิการ',
    contact: 'ติดต่อ', privacy: 'ประกาศความเป็นส่วนตัว', terms: 'ข้อกำหนดการใช้บริการ', social: 'ช่องทางโซเชียลของ Stocktag'
  }
};
Object.assign(FOOTER_COPY_BY_LANGUAGE, {
  bn: PUBLIC_MARKETING_FOOTER_COPY_BN,
  hi: PUBLIC_MARKETING_FOOTER_COPY_HI,
  pt: PUBLIC_MARKETING_FOOTER_COPY_PT
});

const SOCIAL_ICON_BY_ID = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  x: Twitter,
  youtube: Youtube
};

const THEME_LABEL_FALLBACKS = {
  light: 'Light',
  dark: 'Dark'
};

const FOOTER_FLOW_BY_LANGUAGE = {
  en: { label: 'Publishing workflow', input: 'Source images', studio: 'Metadata Studio', review: 'Reviewed metadata', export: 'Files ready to upload' },
  tr: { label: 'Yayınlama süreci', input: 'Kaynak görseller', studio: 'Metadata Studio', review: 'İncelenmiş meta veri', export: 'Yüklemeye hazır çıktılar' },
  es: { label: 'Flujo de publicación', input: 'Archivos originales', studio: 'Metadata Studio', review: 'Metadatos revisados', export: 'Archivos listos para subir' },
  fr: { label: 'De l’image à la publication', input: 'Fichiers source', studio: 'Metadata Studio', review: 'Métadonnées vérifiées', export: 'Fichiers prêts à mettre en ligne' },
  de: { label: 'Workflow bis zum Upload', input: 'Ausgangsdateien', studio: 'Metadata Studio', review: 'Geprüfte Metadaten', export: 'Uploadfertige Dateien' },
  it: { label: 'Workflow di pubblicazione', input: 'File visivi originali', studio: 'Metadata Studio', review: 'Metadati revisionati', export: 'File pronti da esportare' },
  id: { label: 'Alur publikasi', input: 'File visual asli', studio: 'Metadata Studio', review: 'Metadata yang sudah ditinjau', export: 'Hasil ekspor siap pakai' },
  th: { label: 'ขั้นตอนก่อนเผยแพร่', input: 'ไฟล์ภาพต้นฉบับ', studio: 'Metadata Studio', review: 'เมทาดาทาที่ตรวจทานแล้ว', export: 'ไฟล์พร้อมอัปโหลด' }
};
Object.assign(FOOTER_FLOW_BY_LANGUAGE, {
  bn: PUBLIC_MARKETING_FOOTER_FLOW_BN,
  hi: PUBLIC_MARKETING_FOOTER_FLOW_HI,
  pt: PUBLIC_MARKETING_FOOTER_FLOW_PT
});

const isPlainPrimaryClick = (event) => (
  event.button === 0
  && !event.metaKey
  && !event.ctrlKey
  && !event.shiftKey
  && !event.altKey
);

const FooterActionLink = ({ href, onAction, children, ...props }) => {
  if (!href) {
    return <button type="button" {...props} onClick={onAction}>{children}</button>;
  }

  return (
    <a
      href={href}
      {...props}
      onClick={(event) => {
        if (!onAction || !isPlainPrimaryClick(event)) return;
        event.preventDefault();
        onAction(event);
      }}
    >
      {children}
    </a>
  );
};

const PublicMarketingFooter = ({
  language = 'en',
  setLanguage,
  texts,
  theme = 'light',
  toggleTheme,
  isAuthenticated = false,
  authLoading = false,
  accountLabel = '',
  homeHref = '/',
  studioHref = '/metadata',
  loginHref = '/metadata?auth=login',
  accountHref = '/metadata',
  historyHref = '/metadata?view=history',
  pricingHref = '/pricing',
  tourHref = '/metadata',
  onOpenStudio,
  onOpenLogin,
  onOpenAccount,
  onOpenHistory,
  onOpenPricing,
  onStartTour,
  onOpenCookieSettings = openCookieSettings,
  navigationGroups,
  socialLinks = SITE_SOCIAL_LINKS,
  copyrightYear = new Date().getFullYear(),
  copy: copyOverrides,
  hrefLangEntries = [],
  className = ''
}) => {
  const normalizedLanguage = normalizeLanguage(language);
  const localeCopy = FOOTER_COPY_BY_LANGUAGE[normalizedLanguage] || FOOTER_COPY_BY_LANGUAGE.en;
  const copy = { ...localeCopy, ...(copyOverrides || {}) };
  const flowCopy = FOOTER_FLOW_BY_LANGUAGE[normalizedLanguage] || FOOTER_FLOW_BY_LANGUAGE.en;
  const t = (key, fallback) => (texts && texts[key]) || fallback;
  const normalizedTheme = normalizeTheme(theme);
  const appearanceLabel = t('appearance', 'Appearance');
  const currentThemeLabel = t(
    `theme_${normalizedTheme.replace('-', '_')}`,
    THEME_LABEL_FALLBACKS[normalizedTheme]
  );
  const localizedHomeHref = localizeInternalHref(homeHref, language);
  const localizedStudioHref = localizeInternalHref(studioHref, language);
  const localizedPricingHref = localizeInternalHref(pricingHref, language);
  const effectiveAccountHref = localizeInternalHref(isAuthenticated ? accountHref : loginHref, language);
  const effectiveAccountAction = isAuthenticated ? onOpenAccount : onOpenLogin;
  const effectiveAccountLabel = authLoading
    ? t('loading_text', 'Loading')
    : accountLabel || (isAuthenticated
      ? t('top_workspace', t('sidebar_workspace', copy.account))
      : t('sidebar_login', copy.account));

  const defaultNavigationGroups = useMemo(() => ([
    {
      id: 'product',
      label: copy.product,
      links: [
        { label: t('nav_metadata', 'Metadata Studio'), href: PUBLIC_MARKETING_ROUTES.metadata },
        { label: t('nav_market_intelligence', 'Market Intelligence'), href: PUBLIC_MARKETING_ROUTES.marketIntelligence },
        { label: copy.platformExports, href: PUBLIC_MARKETING_ANCHORS.platformExports },
        { label: t('nav_pricing', copy.seePricing), href: pricingHref, onAction: onOpenPricing }
      ]
    },
    {
      id: 'tools',
      label: copy.tools,
      links: [
        { label: t('nav_vectorizer', 'Vectorizer'), href: PUBLIC_MARKETING_ROUTES.vectorizer },
        { label: copy.metadataTools, href: PUBLIC_MARKETING_ANCHORS.coreTools }
      ]
    },
    {
      id: 'resources',
      label: copy.resources,
      links: [
        { label: t('nav_blog', 'Blog'), href: PUBLIC_MARKETING_ROUTES.blog },
        { label: t('nav_free_credits', 'Free Credits'), href: PUBLIC_MARKETING_ROUTES.freeCredits },
        { label: t('nav_help', 'Help'), href: PUBLIC_MARKETING_ROUTES.help },
        { label: t('nav_changelog', 'Changelog'), href: PUBLIC_MARKETING_ROUTES.changelog },
        { label: t('nav_feedback', 'Feedback'), href: PUBLIC_MARKETING_ROUTES.feedback }
      ]
    },
    {
      id: 'account',
      label: copy.account,
      links: [
        { label: effectiveAccountLabel, href: isAuthenticated ? accountHref : loginHref, onAction: effectiveAccountAction, busy: authLoading },
        { label: t('nav_history', 'History'), href: historyHref, onAction: onOpenHistory },
        { label: t('top_tour', 'Tour'), href: tourHref, onAction: onStartTour },
        { label: copy.studio, href: studioHref, onAction: onOpenStudio }
      ]
    },
    {
      id: 'company',
      label: copy.company,
      links: [
        { label: copy.about, href: PUBLIC_MARKETING_ROUTES.about },
        { label: copy.editorial, href: PUBLIC_MARKETING_ROUTES.editorial },
        { label: copy.contact, href: PUBLIC_MARKETING_ROUTES.contact }
      ]
    }
  ]), [
    accountHref,
    authLoading,
    copy,
    effectiveAccountAction,
    effectiveAccountLabel,
    historyHref,
    isAuthenticated,
    loginHref,
    onOpenHistory,
    onOpenPricing,
    onOpenStudio,
    onStartTour,
    pricingHref,
    studioHref,
    texts,
    tourHref
  ]);
  const resolvedNavigationGroups = navigationGroups || defaultNavigationGroups;

  return (
    <footer className={`public-marketing-footer ${className}`.trim()} data-public-marketing-footer="true">
      <div className="pm-footer-inner">
        <section className="pm-footer-cta" aria-labelledby="pm-footer-cta-title">
          <div className="pm-footer-cta-copy">
            <span className="pm-footer-eyebrow">{copy.ctaEyebrow}</span>
            <h2 id="pm-footer-cta-title">{copy.ctaTitle}</h2>
            <p>{copy.ctaBody}</p>
          </div>
          <div className="pm-footer-cta-actions">
            <FooterActionLink
              className="pm-footer-studio-cta"
              href={localizedStudioHref}
              onAction={onOpenStudio}
            >
              <Sparkles size={17} aria-hidden="true" />
              <span>{copy.studio}</span>
              <ArrowRight size={16} aria-hidden="true" />
            </FooterActionLink>
            <FooterActionLink
              className="pm-footer-pricing-cta"
              href={localizedPricingHref}
              onAction={onOpenPricing}
            >
              {copy.seePricing}
            </FooterActionLink>
          </div>
          <div className="pm-footer-workflow" aria-label={flowCopy.label}>
            <span data-footer-workflow-step><Files size={17} aria-hidden="true" /><strong>{flowCopy.input}</strong></span>
            <ArrowRight size={15} aria-hidden="true" />
            <span className="is-studio" data-footer-workflow-step><Tags size={17} aria-hidden="true" /><strong>{flowCopy.studio}</strong></span>
            <ArrowRight size={15} aria-hidden="true" />
            <span data-footer-workflow-step><FileCheck2 size={17} aria-hidden="true" /><strong>{flowCopy.review}</strong></span>
            <ArrowRight size={15} aria-hidden="true" />
            <span data-footer-workflow-step><PackageCheck size={17} aria-hidden="true" /><strong>{flowCopy.export}</strong></span>
          </div>
        </section>

        <div className="pm-footer-main">
          <div className="pm-footer-brand-column">
            <a className="pm-footer-brand" href={localizedHomeHref} aria-label="Stocktag.AI">
              <StocktagBrandLogo variant="full" decorative />
            </a>
            <p>{copy.tagline}</p>
            {socialLinks?.length ? (
              <div className="pm-footer-social" aria-label={copy.social}>
                {socialLinks.filter((item) => item?.href).map((item) => {
                  const Icon = SOCIAL_ICON_BY_ID[item.id];
                  return (
                    <a
                      key={item.id || item.href}
                      href={item.href}
                      target="_blank"
                      rel="me noopener noreferrer"
                      aria-label={item.label}
                      title={item.label}
                    >
                      {Icon ? <Icon size={18} aria-hidden="true" /> : item.label}
                    </a>
                  );
                })}
              </div>
            ) : null}
            <div className="pm-footer-preferences">
              <label className="pm-footer-preference-row pm-footer-language">
                <span className="pm-footer-preference-label">
                  <Globe2 size={16} aria-hidden="true" />
                  <span>{t('languageLabel', 'Language')}</span>
                </span>
                <select
                  name="footer-language"
                  autoComplete="off"
                  value={normalizedLanguage}
                  onChange={(event) => setLanguage?.(event.target.value)}
                  disabled={!setLanguage}
                  aria-label={t('languageLabel', 'Language')}
                >
                  {LANGUAGE_OPTIONS.map((option) => (
                    <option key={option.code} value={option.code}>{option.label}</option>
                  ))}
                </select>
              </label>
              <div
                className="pm-footer-preference-row pm-footer-theme"
                role="group"
                aria-label={`${appearanceLabel}: ${currentThemeLabel}`}
              >
                <span className="pm-footer-preference-label">
                  <Palette size={16} aria-hidden="true" />
                  <span>{appearanceLabel}</span>
                </span>
                <div className="pm-footer-theme-action">
                  <span className="pm-footer-theme-name" aria-live="polite">{currentThemeLabel}</span>
                  {toggleTheme ? (
                    <ThemeToggle
                      theme={normalizedTheme}
                      toggleTheme={toggleTheme}
                      texts={texts}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <nav className="pm-footer-navigation" aria-label={copy.footerNavigation}>
            {resolvedNavigationGroups.map((group, groupIndex) => {
              const groupId = group.id || `group-${groupIndex + 1}`;
              return (
                <section key={groupId} aria-labelledby={`pm-footer-group-${groupId}`}>
                  <h3 id={`pm-footer-group-${groupId}`}>{group.label}</h3>
                  <ul>
                    {group.links.map((link) => (
                      <li key={`${link.label}-${link.href}`}>
                        <FooterActionLink
                          href={localizeSeoHref(link.href, language)}
                          onAction={link.onAction}
                          aria-busy={link.busy ? 'true' : undefined}
                        >
                          {link.label}
                        </FooterActionLink>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </nav>
        </div>

        <LanguageAlternatesNav
          hrefLangEntries={hrefLangEntries}
          currentLanguage={normalizedLanguage}
          className="language-alternates--marketing-footer"
        />

        <div className="pm-footer-bottom">
          <p>© {copyrightYear} Stocktag.AI</p>
          <nav className="pm-footer-legal" aria-label={copy.legal}>
            <a href={localizeSeoHref(PUBLIC_MARKETING_ROUTES.privacy, language)}>{t('auth_privacy', copy.privacy)}</a>
            <a href={localizeSeoHref(PUBLIC_MARKETING_ROUTES.terms, language)}>{t('auth_terms', copy.terms)}</a>
            <button type="button" onClick={onOpenCookieSettings}>
              {t('cookie_settings', 'Cookie settings')}
            </button>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default PublicMarketingFooter;
